import { brandBrainContentSchema } from './brand-brain.js';
/** Review order, and it is a product decision rather than an arbitrary
 *  one. §3.1b put identity ahead of tags: an unconfirmed founder blocks
 *  more downstream work than an unconfirmed tag. Plans sit directly
 *  after products because they are the most commercially load-bearing
 *  rows on the brain — the campaign built on a plan that did not exist
 *  is what put this whole surface on the roadmap — but behind identity
 *  for the same §3.1b reason. */
export const DRAFTABLE_SECTIONS = [
    {
        kind: 'team',
        brainKey: 'team',
        label: () => 'team',
        select: (b) => b.team ?? [],
    },
    {
        kind: 'product',
        brainKey: 'products',
        label: (n) => `${n} product${n === 1 ? '' : 's'}`,
        select: (b) => b.products ?? [],
    },
    {
        kind: 'pricingPlan',
        brainKey: 'pricing',
        label: (n) => `${n} plan${n === 1 ? '' : 's'}`,
        // The one section whose rows are nested rather than a top-level
        // array — pricing is { model, plans } because the model is a
        // property of the brand, not of any one tier.
        select: (b) => b.pricing?.plans ?? [],
    },
    {
        // Not a row Mag extracted and wants confirmed — a QUESTION the
        // founder's own pricing page asks that she could not resolve on
        // their behalf. It sits in the same queue because it is the same
        // ask ("this is what I could not be sure of, tell me") and the
        // founder should not have to learn a second surface for it.
        //
        // Shares brainKey 'pricing' with the plans above. The registry
        // tolerates that — brainKey is read as a Set by the exhaustiveness
        // test and nothing keys a save patch on it.
        kind: 'pricingCondition',
        brainKey: 'pricing',
        label: (n) => `${n} pricing question${n === 1 ? '' : 's'}`,
        select: (b) => b.pricing?.conditions ?? [],
    },
    {
        kind: 'testimonial',
        brainKey: 'testimonials',
        label: (n) => `${n} quote${n === 1 ? '' : 's'}`,
        select: (b) => b.testimonials ?? [],
    },
    {
        kind: 'voiceExample',
        brainKey: 'voiceExamples',
        label: () => 'voice',
        select: (b) => b.voiceExamples ?? [],
    },
];
/** Brain sections that carry no draft rows, and why. Kept as data rather
 *  than as an absence so the exhaustiveness test can tell "considered
 *  and excluded" from "forgotten" — the distinction the pricing gap
 *  turned on. */
export const NON_DRAFTABLE_BRAIN_SECTIONS = {
    identity: 'a single object the founder edits directly — no per-row extraction',
    voice: 'single object; the draftable per-row voice library is voiceExamples',
    customer: 'single object; its legacy testimonials array is superseded by the testimonials section',
    assets: 'files, confirmed by uploading or deleting them rather than by a status flag',
    history: 'append-only record of what happened; nothing to confirm',
    preferences: 'founder-authored by definition, so never extracted as a draft',
    designSystem: 'a single object with its own workbench editor and no row status',
};
/** The number of chips the needs-you card can carry. Derived, because
 *  the cap used to be the literal 4 — exactly the section count of the
 *  day — and pricing made five. A brain with drafts everywhere would
 *  have failed payload validation and emitted NO card, worst for the
 *  founders with the most to confirm. */
export const MAX_DRAFT_CARD_PARTS = DRAFTABLE_SECTIONS.length;
/** The single definition of "this row is still waiting on the founder".
 *  The needs-you card, the confirm queue and the workbench dots all
 *  call it, so they cannot drift into reporting different amounts of
 *  outstanding work — a dot that outlives its card teaches the founder
 *  to ignore both. */
export function isDraftRow(row) {
    return row.status === 'draft';
}
/** Outstanding rows per section kind. Sections with none are present
 *  with 0, so callers can render a stable set of keys. */
export function countDraftsBySection(brain) {
    const out = {};
    for (const section of DRAFTABLE_SECTIONS) {
        out[section.kind] = brain ? section.select(brain).filter(isDraftRow).length : 0;
    }
    return out;
}
/** Total outstanding rows plus the card's chips, in review order. */
export function summarizeBrainDrafts(brain) {
    const counts = countDraftsBySection(brain);
    const parts = [];
    let total = 0;
    for (const section of DRAFTABLE_SECTIONS) {
        const n = counts[section.kind];
        if (n <= 0)
            continue;
        total += n;
        parts.push(section.label(n));
    }
    return { total, parts };
}
/** Every key of BrandBrainContent, for the exhaustiveness test. Read off
 *  the schema rather than hand-listed — the point is to notice a section
 *  the author of this file has never seen. */
export function brainSectionKeys() {
    return Object.keys(brandBrainContentSchema.shape);
}
//# sourceMappingURL=brand-brain-drafts.js.map